import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { collection, doc, getDoc, getDocs, query, serverTimestamp, setDoc, where } from "firebase/firestore";
import { db } from "./firebase";
import { sendUserNotification } from "./notificationApi";
import RazorpayButton from "./RazorpayButton";
import { BOOKING_SLOT_OPTIONS, buildDateSlotKey } from "./bookingSlots";

const maskOwner = (email) => {
  const value = String(email || "").trim();
  if (!value.includes("@")) return "Pet Owner";
  const [local] = value.split("@");
  if (!local) return "Pet Owner";
  return `${local.slice(0, 1)}${"*".repeat(Math.max(2, local.length - 1))}`;
};
export default function ProviderProfilePage({
  service,
  user,
  selectedPet,
  onBack,
  onLoginClick,
}) {
  const serviceId = service?.id || "";
  const [reviews, setReviews] = useState([]);
  const [providerProfile, setProviderProfile] = useState(null);
  const [reportingReviewId, setReportingReviewId] = useState("");
  const [quickPetDetails, setQuickPetDetails] = useState({
    name: "",
    species: "",
    age: "",
    weight: "",
    notes: "",
  });
  const [selectedBookingDate, setSelectedBookingDate] = useState(
    () => new Date().toISOString().slice(0, 10),
  );
  const [selectedBookingEndDate, setSelectedBookingEndDate] = useState(
    () => new Date().toISOString().slice(0, 10),
  );
  const [selectedBookingSlot, setSelectedBookingSlot] = useState(BOOKING_SLOT_OPTIONS[0]);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const bookingPanelRef = useRef(null);

  useEffect(() => {
    const loadReviews = async () => {
      if (!serviceId) {
        setReviews([]);
        return;
      }
      try {
        const q = query(
          collection(db, "reviews"),
          where("serviceId", "==", serviceId),
        );
        const snap = await getDocs(q);
        const list = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((review) => review.hidden !== true);
        list.sort((a, b) => (b.updatedAt?.seconds || 0) - (a.updatedAt?.seconds || 0));
        setReviews(list);
      } catch (error) {
        setReviews([]);
      }
    };

    loadReviews();
  }, [serviceId]);

  useEffect(() => {
    const loadProviderProfile = async () => {
      const providerId = service?.providerId || "";
      if (!providerId) {
        setProviderProfile(null);
        return;
      }
      try {
        const snap = await getDoc(doc(db, "users", providerId));
        setProviderProfile(snap.exists() ? snap.data() : null);
      } catch (error) {
        setProviderProfile(null);
      }
    };
    loadProviderProfile();
  }, [service?.providerId]);

  const avgRating = useMemo(() => {
    if (reviews.length === 0) return 0;
    const total = reviews.reduce((sum, r) => sum + (Number(r.rating) || 0), 0);
    return total / reviews.length;
  }, [reviews]);
  const quickDateOptions = useMemo(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const items = [];
    for (let index = 0; index < 7; index += 1) {
      const valueDate = new Date(start);
      valueDate.setDate(start.getDate() + index);
      items.push({
        iso: valueDate.toISOString().slice(0, 10),
        label: valueDate.toLocaleDateString("en-IN", {
          weekday: "short",
          day: "numeric",
          month: "short",
        }),
      });
    }
    return items;
  }, []);
  const isBoardingService = /boarding|daycare|sitting|pet hotel|hotel/i.test(
    `${service?.title || ""} ${service?.category || ""}`,
  );
  const blockedDates = useMemo(
    () => (Array.isArray(providerProfile?.unavailableDates) ? providerProfile.unavailableDates : []),
    [providerProfile?.unavailableDates],
  );
  const blockedSlotKeys = useMemo(
    () =>
      (Array.isArray(providerProfile?.unavailableSlots) ? providerProfile.unavailableSlots : [])
        .map((value) => String(value || "").trim().toLowerCase())
        .filter(Boolean),
    [providerProfile?.unavailableSlots],
  );
  const availableSlotsForSelectedDate = useMemo(
    () =>
      BOOKING_SLOT_OPTIONS.filter((slot) => {
        const key = buildDateSlotKey(selectedBookingDate, slot);
        return key ? !blockedSlotKeys.includes(key) : true;
      }),
    [blockedSlotKeys, selectedBookingDate],
  );
  const isSelectedDateBlocked = blockedDates.includes(selectedBookingDate);
  const isSelectedSlotBlocked = Boolean(
    selectedBookingDate &&
    selectedBookingSlot &&
    blockedSlotKeys.includes(buildDateSlotKey(selectedBookingDate, selectedBookingSlot)),
  );
  const isBookingSelectionBlocked =
    isSelectedDateBlocked ||
    !selectedBookingSlot ||
    isSelectedSlotBlocked ||
    availableSlotsForSelectedDate.length === 0;
  const bookingBlockedReason = isSelectedDateBlocked ?
    "Selected date is unavailable for this provider." :
    availableSlotsForSelectedDate.length === 0 ?
      "No slots available on selected date. Please choose another date." :
      isSelectedSlotBlocked ?
        "Selected slot is unavailable. Please choose another slot." :
        "";

  useEffect(() => {
    if (!isBoardingService) return;
    if (!selectedBookingEndDate || selectedBookingEndDate < selectedBookingDate) {
      setSelectedBookingEndDate(selectedBookingDate);
    }
  }, [isBoardingService, selectedBookingDate, selectedBookingEndDate]);

  useEffect(() => {
    if (!availableSlotsForSelectedDate.length) {
      if (selectedBookingSlot) {
        setSelectedBookingSlot("");
      }
      return;
    }
    if (!availableSlotsForSelectedDate.includes(selectedBookingSlot)) {
      setSelectedBookingSlot(availableSlotsForSelectedDate[0]);
    }
  }, [availableSlotsForSelectedDate, selectedBookingSlot]);
  const photoUrls = useMemo(
    () => (Array.isArray(service.imageUrls) ? service.imageUrls.filter(Boolean) : []),
    [service.imageUrls],
  );
  const heroImage = photoUrls[0] || "/petaar-logo.jpeg";
  const isBookingDateRangeInvalid = isBoardingService && selectedBookingEndDate < selectedBookingDate;
  const isDateRangeBlocked = useMemo(() => {
    if (!isBoardingService) return false;
    const start = String(selectedBookingDate || "").trim();
    const end = String(selectedBookingEndDate || "").trim();
    if (!start || !end || end < start) return false;
    const blocked = new Set((Array.isArray(blockedDates) ? blockedDates : []).map((d) => String(d || "").trim()));
    const cursor = new Date(`${start}T00:00:00`);
    const max = new Date(`${end}T00:00:00`);
    while (cursor <= max) {
      const key = cursor.toISOString().slice(0, 10);
      if (blocked.has(key)) return true;
      cursor.setDate(cursor.getDate() + 1);
    }
    return false;
  }, [blockedDates, isBoardingService, selectedBookingDate, selectedBookingEndDate]);
  const bookingPetDetails = {
    name: selectedPet?.name || quickPetDetails.name,
    species: selectedPet?.species || quickPetDetails.species,
    breed: selectedPet?.breed || "",
    age: selectedPet?.age || quickPetDetails.age,
    weight: selectedPet?.weight || quickPetDetails.weight,
    notes: quickPetDetails.notes,
  };
  const galleryImages = useMemo(
    () => (photoUrls.length > 0 ? photoUrls : [heroImage]).filter(Boolean),
    [photoUrls, heroImage],
  );

  const openGalleryAt = (index) => {
    if (!galleryImages.length) return;
    const normalized = Math.max(0, Math.min(index, galleryImages.length - 1));
    setGalleryIndex(normalized);
    setGalleryOpen(true);
  };
  const closeGallery = () => setGalleryOpen(false);
  const showNextGalleryImage = useCallback(() => {
    if (!galleryImages.length) return;
    setGalleryIndex((prev) => (prev + 1) % galleryImages.length);
  }, [galleryImages.length]);
  const showPrevGalleryImage = useCallback(() => {
    if (!galleryImages.length) return;
    setGalleryIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  }, [galleryImages.length]);

  useEffect(() => {
    if (!galleryOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        closeGallery();
        return;
      }
      if (event.key === "ArrowRight") {
        showNextGalleryImage();
      } else if (event.key === "ArrowLeft") {
        showPrevGalleryImage();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [galleryOpen, showNextGalleryImage, showPrevGalleryImage]);

  useEffect(() => {
    if (!galleryOpen || typeof document === "undefined") return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [galleryOpen]);

  if (!service) {
    return null;
  }

  const reportReview = async (review) => {
    if (!review?.id) return;
    if (!user?.uid) {
      onLoginClick(service);
      return;
    }
    const reason = window.prompt("Reason to report this review? (e.g., abusive language, spam, fake)");
    const cleanedReason = String(reason || "").trim();
    if (!cleanedReason) return;
    if (cleanedReason.length > 300) {
      alert("Please keep reason under 300 characters.");
      return;
    }
    try {
      setReportingReviewId(review.id);
      await setDoc(doc(db, "reviewReports", `${review.id}_${user.uid}`), {
        reviewId: review.id,
        serviceId: service.id || "",
        serviceTitle: service.title || "",
        providerEmail: service.providerEmail || "",
        ownerEmail: review.ownerEmail || "",
        reporterUid: user.uid,
        reporterEmail: user.email || "",
        reason: cleanedReason,
        status: "pending",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }, { merge: true });
      await sendUserNotification({
        recipientEmail: user.email || "",
        type: "review_report",
        title: "Review report submitted",
        message: `Your report for review on ${service.title || "service"} has been submitted.`,
      });
      alert("Report submitted. Admin team will review it.");
    } catch (error) {
      alert(error.message || "Unable to report review.");
    } finally {
      setReportingReviewId("");
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(180deg, #f2f7ff 0%, #eef6ff 60%, #f8fbff 100%)",
        padding: 24,
      }}
    >
      <button
        onClick={onBack}
        style={{
          width: 44,
          height: 44,
          borderRadius: "50%",
          border: "none",
          background: "#fff",
          boxShadow: "0 8px 18px rgba(15,23,42,0.12)",
          cursor: "pointer",
          fontSize: 22,
          color: "#1e3a8a",
        }}
        aria-label="Back to Services"
      >
        ←
      </button>

      <div style={{ maxWidth: 1100, margin: "16px auto 0" }}>
        <div
          style={{
            position: "relative",
            background: "linear-gradient(120deg, #ffffff 0%, #eef6ff 60%, #f7f9ff 100%)",
            borderRadius: 26,
            padding: 18,
            border: "1px solid #d6e4ff",
            boxShadow: "0 18px 36px rgba(59, 130, 246, 0.18)",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: 16,
            alignItems: "center",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: 26,
              boxShadow: "0 0 0 4px rgba(186, 209, 255, 0.35) inset",
              pointerEvents: "none",
            }}
          />
          <div style={{ position: "absolute", right: 18, top: 14, color: "#f59e0b", fontSize: 16 }}>
            ★ {avgRating ? avgRating.toFixed(1) : "4.9"} ({reviews.length || 245} reviews)
          </div>
          <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
            <div
              style={{
                width: 120,
                height: 120,
                borderRadius: 22,
                overflow: "hidden",
                background: "#fff",
                border: "3px solid #fff",
                boxShadow: "0 10px 18px rgba(30,58,138,0.18)",
              }}
            >
              <img
                src={heroImage}
                alt={service.title}
                style={{ width: "100%", height: "100%", objectFit: "cover", cursor: "zoom-in" }}
                onClick={() => openGalleryAt(0)}
              />
            </div>
            <div>
              {service.isFeatured && service.featuredUntil &&
                new Date(service.featuredUntil).getTime() > Date.now() && (
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    background: "#ffe9bf",
                    border: "1px solid #f5c66a",
                    color: "#7a4b00",
                    padding: "4px 10px",
                    borderRadius: 999,
                    marginBottom: 6,
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  🐾 Featured Provider
                </div>
              )}
              <h2 style={{ margin: "0 0 6px", color: "#0f172a" }}>{service.title}</h2>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 6 }}>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    background: "#ecfdf3",
                    color: "#15803d",
                    border: "1px solid #86efac",
                    borderRadius: 999,
                    padding: "2px 10px",
                    fontWeight: 700,
                    fontSize: 12,
                  }}
                >
                  ✓ Verified
                </span>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    background: "#fef3c7",
                    color: "#92400e",
                    border: "1px solid #fcd34d",
                    borderRadius: 999,
                    padding: "2px 10px",
                    fontWeight: 700,
                    fontSize: 12,
                  }}
                >
                  ⭐ {avgRating ? avgRating.toFixed(1) : "4.9"} rating
                </span>
              </div>
              <div style={{ display: "grid", gap: 4, color: "#334155", fontSize: 14 }}>
                <div>Category: {service.category}</div>
                <div>📍 {service.city}</div>
                <div>⏱ {service.availabilityNote || "Mon-Sat"}</div>
              </div>
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <div
              style={{
                padding: "12px 16px",
                borderRadius: 20,
                background: "linear-gradient(135deg, #60a5fa 0%, #a855f7 55%, #f472b6 100%)",
                color: "#fff",
                fontWeight: 800,
                minWidth: 140,
                textAlign: "center",
                boxShadow: "0 12px 22px rgba(29,78,216,0.25)",
              }}
            >
              <div style={{ fontSize: 12, opacity: 0.9 }}>Starting from</div>
              <div style={{ fontSize: 22 }}>₹{service.price}</div>
              <button
                onClick={() => bookingPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
                style={{
                  marginTop: 8,
                  width: "100%",
                  border: "none",
                  borderRadius: 999,
                  padding: "6px 10px",
                  background: "#22c55e",
                  color: "#fff",
                  fontWeight: 800,
                  cursor: "pointer",
                }}
              >
                Order Now
              </button>
            </div>
          </div>
        </div>

        <div
          style={{
            marginTop: 16,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 12,
          }}
        >
          {[
            { label: "Experience", value: providerProfile?.yearsExperience || "8+ years" },
            { label: "Completed Jobs", value: "500+" },
            { label: "Avg Response", value: providerProfile?.avgResponseMinutes ? `~ ${providerProfile.avgResponseMinutes} mins` : "< 30 mins" },
            { label: "Secure Payments", value: "Protected" },
          ].map((item) => (
            <div
              key={item.label}
              style={{
                background: "linear-gradient(180deg, #ffffff 0%, #f7fbff 100%)",
                borderRadius: 16,
                border: "1px solid #e2e8f0",
                padding: 12,
                boxShadow: "0 8px 18px rgba(15,23,42,0.08)",
              }}
            >
              <div style={{ color: "#64748b", fontSize: 12 }}>{item.label}</div>
              <div style={{ fontWeight: 800, fontSize: 16, color: "#0f172a" }}>{item.value}</div>
            </div>
          ))}
        </div>

        <div
          style={{
            marginTop: 16,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: 16,
          }}
        >
          <div style={{ display: "grid", gap: 16 }}>
            <div
              style={{
                background: "#fff",
                borderRadius: 16,
                border: "1px solid #e2e8f0",
                padding: 16,
                boxShadow: "0 10px 20px rgba(15,23,42,0.06)",
              }}
            >
              <h3 style={{ marginTop: 0 }}>About This Service</h3>
              <p style={{ marginBottom: 0 }}>
                {providerProfile?.about || "Trusted pet care designed for comfort, safety, and fast support."}
              </p>
            </div>

            <div
              style={{
                background: "#fff",
                borderRadius: 16,
                border: "1px solid #e2e8f0",
                padding: 16,
                boxShadow: "0 10px 20px rgba(15,23,42,0.06)",
              }}
            >
              <h3 style={{ marginTop: 0 }}>Photo Gallery</h3>
              <small style={{ display: "block", marginTop: -8, marginBottom: 10, color: "#64748b" }}>
                Tap any photo to view full size.
              </small>
              <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))" }}>
                {photoUrls.length > 0 ? (
                  photoUrls.map((url, idx) => (
                    <img
                      key={`${url}-${idx}`}
                      src={url}
                      alt={`Service ${idx + 1}`}
                      style={{ width: "100%", height: 90, objectFit: "cover", borderRadius: 10, border: "1px solid #e2e8f0", cursor: "zoom-in" }}
                      onClick={() => openGalleryAt(idx)}
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  ))
                ) : (
                  <>
                    <div style={{ width: "100%", height: 90, background: "#f0f0f0", borderRadius: 10 }} />
                    <div style={{ width: "100%", height: 90, background: "#f0f0f0", borderRadius: 10 }} />
                    <div style={{ width: "100%", height: 90, background: "#f0f0f0", borderRadius: 10 }} />
                  </>
                )}
              </div>
            </div>

            <div
              style={{
                background: "#fff",
                borderRadius: 16,
                border: "1px solid #e2e8f0",
                padding: 16,
                boxShadow: "0 10px 20px rgba(15,23,42,0.06)",
              }}
            >
              <h3 style={{ marginTop: 0 }}>Customer Reviews</h3>
              <p style={{ marginTop: 0 }}>
                Average Rating: <strong>{(avgRating || 0).toFixed(1)}/5</strong> ({reviews.length} reviews)
              </p>
              {reviews.length === 0 && <p style={{ marginTop: 0, color: "#666" }}>No reviews yet.</p>}
              {reviews.map((review) => (
                <div key={review.id} style={{ marginBottom: 8 }}>
                  <strong>{maskOwner(review.ownerEmail)}</strong> - {review.rating}/5
                  {review.verifiedBooking && (
                    <small
                      style={{
                        marginLeft: 8,
                        border: "1px solid #86efac",
                        color: "#166534",
                        background: "#dcfce7",
                        borderRadius: 999,
                        padding: "1px 8px",
                      }}
                    >
                      Verified booking
                    </small>
                  )}
                  <p style={{ margin: "4px 0 0" }}>{review.comment}</p>
                  {review.providerReply && (
                    <div style={{ marginTop: 6, border: "1px solid #dbe3ee", borderRadius: 8, padding: 8, background: "#f8fafc" }}>
                      <strong>Provider Reply</strong>
                      <p style={{ margin: "4px 0 0" }}>{review.providerReply}</p>
                    </div>
                  )}
                  <div style={{ marginTop: 6 }}>
                    <button onClick={() => reportReview(review)} disabled={reportingReviewId === review.id}>
                      {reportingReviewId === review.id ? "Reporting..." : "Report Review"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div
            ref={bookingPanelRef}
            style={{
              background: "linear-gradient(140deg, #ffffff 0%, #f7fbff 58%, #f3f8ff 100%)",
              borderRadius: 22,
              border: "1px solid #d9e6ff",
              padding: "18px 18px 16px",
              boxShadow: "0 16px 28px rgba(37, 99, 235, 0.14)",
              height: "fit-content",
            }}
          >
            <h3 style={{ marginTop: 0, marginBottom: 10, color: "#1e293b", fontSize: 34 }}>Choose Booking Slot</h3>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {quickDateOptions.map((option) => (
                <button
                  key={`date-chip-${option.iso}`}
                  type="button"
                  onClick={() => setSelectedBookingDate(option.iso)}
                  style={{
                    border: selectedBookingDate === option.iso ? "1px solid #2563eb" : "1px solid #cad7ec",
                    borderRadius: 999,
                    background: selectedBookingDate === option.iso ? "#dbeafe" : "#f8fbff",
                    color: "#1e293b",
                    fontWeight: 700,
                    padding: "7px 12px",
                    cursor: "pointer",
                    boxShadow: selectedBookingDate === option.iso ? "0 6px 12px rgba(37,99,235,0.18)" : "none",
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: isBoardingService ? "repeat(2, minmax(180px, 1fr))" : "minmax(180px, 280px)", gap: 10, marginTop: 12 }}>
              <div>
                <small style={{ color: "#64748b", display: "block", marginBottom: 4, fontWeight: 600 }}>
                  {isBoardingService ? "Start date" : "Date"}
                </small>
                <input
                  type="date"
                  min={new Date().toISOString().slice(0, 10)}
                  value={selectedBookingDate}
                  onChange={(e) => setSelectedBookingDate(e.target.value)}
                  style={{ width: "100%", border: "1px solid #c9d6ea", borderRadius: 6, padding: "8px 10px" }}
                />
              </div>
              {isBoardingService && (
                <div>
                  <small style={{ color: "#64748b", display: "block", marginBottom: 4, fontWeight: 600 }}>End date</small>
                  <input
                    type="date"
                    min={selectedBookingDate || new Date().toISOString().slice(0, 10)}
                    value={selectedBookingEndDate}
                    onChange={(e) => setSelectedBookingEndDate(e.target.value)}
                    style={{ width: "100%", border: "1px solid #c9d6ea", borderRadius: 6, padding: "8px 10px" }}
                  />
                </div>
              )}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12 }}>
              {BOOKING_SLOT_OPTIONS.map((slot) => {
                const slotBlocked = !availableSlotsForSelectedDate.includes(slot);
                const selected = selectedBookingSlot === slot;
                return (
                  <button
                    key={`slot-chip-${slot}`}
                    type="button"
                    disabled={slotBlocked}
                    onClick={() => setSelectedBookingSlot(slot)}
                    style={{
                      border: selected ? "1px solid #2563eb" : "1px solid #cbd5e1",
                      borderRadius: 999,
                      background: selected ? "#dbeafe" : "#f9fbff",
                      color: slotBlocked ? "#94a3b8" : "#1e293b",
                      fontWeight: 700,
                      padding: "8px 14px",
                      cursor: slotBlocked ? "not-allowed" : "pointer",
                      opacity: slotBlocked ? 0.55 : 1,
                    }}
                  >
                    {slot}
                  </button>
                );
              })}
            </div>
            {(isBookingSelectionBlocked || isBookingDateRangeInvalid || isDateRangeBlocked) && (
              <small style={{ display: "block", marginTop: 8, color: "#b91c1c" }}>
                {isBookingDateRangeInvalid ?
                  "End date must be same or after start date." :
                  isDateRangeBlocked ?
                    "One or more dates in this range are unavailable for this provider." :
                    bookingBlockedReason}
              </small>
            )}

            <div
              style={{
                marginTop: 14,
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                gap: 12,
                alignItems: "stretch",
              }}
            >
              <div
                style={{
                  border: "1px solid #dbe6f8",
                  borderRadius: 18,
                  background: "rgba(255,255,255,0.9)",
                  padding: 14,
                }}
              >
                {user ? (
                  <>
                    {!selectedPet && (
                      <>
                        <strong style={{ display: "flex", alignItems: "center", gap: 8, color: "#1e293b", fontSize: 28 }}>
                          🐾 Quick Pet Details
                        </strong>
                        <p style={{ margin: "6px 0 10px", color: "#64748b" }}>
                          Optional, but recommended for faster and safer service delivery.
                        </p>
                        <div style={{ display: "grid", gap: 8, gridTemplateColumns: "repeat(2, minmax(120px, 1fr))" }}>
                          <input
                            placeholder="Pet name"
                            value={quickPetDetails.name}
                            onChange={(e) => setQuickPetDetails((prev) => ({ ...prev, name: e.target.value }))}
                            style={{ border: "1px solid #c9d6ea", borderRadius: 6, padding: "8px 10px" }}
                          />
                          <select
                            value={quickPetDetails.species}
                            onChange={(e) => setQuickPetDetails((prev) => ({ ...prev, species: e.target.value }))}
                            style={{ border: "1px solid #c9d6ea", borderRadius: 6, padding: "8px 10px" }}
                          >
                            <option value="">Species</option>
                            <option value="Dog">Dog</option>
                            <option value="Cat">Cat</option>
                            <option value="Other">Other</option>
                          </select>
                          <input
                            placeholder="Weight"
                            value={quickPetDetails.weight}
                            onChange={(e) => setQuickPetDetails((prev) => ({ ...prev, weight: e.target.value }))}
                            style={{ border: "1px solid #c9d6ea", borderRadius: 6, padding: "8px 10px" }}
                          />
                          <input
                            placeholder="Age"
                            value={quickPetDetails.age}
                            onChange={(e) => setQuickPetDetails((prev) => ({ ...prev, age: e.target.value }))}
                            style={{ border: "1px solid #c9d6ea", borderRadius: 6, padding: "8px 10px" }}
                          />
                        </div>
                        <textarea
                          placeholder="Additional notes"
                          value={quickPetDetails.notes}
                          onChange={(e) => setQuickPetDetails((prev) => ({ ...prev, notes: e.target.value }))}
                          style={{ marginTop: 8, width: "100%", minHeight: 74, border: "1px solid #c9d6ea", borderRadius: 8, padding: "8px 10px" }}
                        />
                      </>
                    )}
                    {selectedPet && (
                      <div style={{ border: "1px solid #dbe2ec", borderRadius: 12, padding: 10, marginBottom: 10, background: "#fff" }}>
                        <strong>Booking Pet</strong>
                        <p style={{ margin: "6px 0" }}>
                          {selectedPet.name || "Pet"} | {selectedPet.species || "N/A"} | {selectedPet.breed || "N/A"}
                        </p>
                      </div>
                    )}
                    <RazorpayButton
                      service={service}
                      selectedPet={selectedPet}
                      manualPetDetails={bookingPetDetails}
                      blockedDates={blockedDates}
                      blockedSlots={providerProfile?.unavailableSlots || []}
                      prefilledBookingDate={selectedBookingDate}
                      bookingSlot={selectedBookingSlot}
                      bookingEndDate={isBoardingService ? selectedBookingEndDate : ""}
                      hideDateInput
                      externallyDisabled={isBookingSelectionBlocked || isBookingDateRangeInvalid || isDateRangeBlocked}
                      disabledReason={
                        isBookingDateRangeInvalid ?
                          "End date must be same or after start date." :
                          isDateRangeBlocked ?
                            "One or more dates in this range are unavailable for this provider." :
                            bookingBlockedReason
                      }
                      buttonStyle={{
                        border: "none",
                        borderRadius: 999,
                        background: "linear-gradient(135deg,#3b82f6 0%, #1d4ed8 100%)",
                        color: "#fff",
                        fontWeight: 800,
                        padding: "10px 28px",
                        fontSize: 26,
                        boxShadow: "0 10px 18px rgba(37,99,235,0.28)",
                      }}
                      containerStyle={{ marginTop: 12 }}
                    />
                  </>
                ) : (
                  <button
                    onClick={() => onLoginClick(service)}
                    style={{
                      border: "none",
                      borderRadius: 999,
                      background: "linear-gradient(135deg,#3b82f6 0%, #1d4ed8 100%)",
                      color: "#fff",
                      fontWeight: 800,
                      padding: "10px 28px",
                      fontSize: 16,
                      cursor: "pointer",
                    }}
                  >
                    Login to Book
                  </button>
                )}
              </div>

              <div
                style={{
                  border: "1px solid #dbe6f8",
                  borderRadius: 18,
                  minHeight: 260,
                  overflow: "hidden",
                  background: "linear-gradient(180deg, #f8fbff 0%, #f1f7ff 100%)",
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: "center",
                  padding: 8,
                }}
              >
                <img
                  src={photoUrls[1] || heroImage}
                  alt="Pet care"
                  style={{ width: "100%", height: "100%", maxHeight: 270, objectFit: "contain", cursor: "zoom-in" }}
                  onClick={() => openGalleryAt(Math.min(1, galleryImages.length - 1))}
                />
              </div>
            </div>

            {!selectedPet && (
              <p style={{ marginTop: 10, color: "#475569", display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: 18, color: "#14b8a6" }}>🐾</span>
                <strong>Tip:</strong> add/select pet details in <strong>My Pets</strong> for faster booking confirmation.
              </p>
            )}
          </div>
        </div>
      </div>
      {galleryOpen && (
        <div
          onClick={closeGallery}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 5000,
            background: "rgba(15, 23, 42, 0.86)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
        >
          <div
            onClick={(event) => event.stopPropagation()}
            style={{
              position: "relative",
              width: "min(96vw, 1080px)",
              maxHeight: "92vh",
              borderRadius: 14,
              background: "#0b1324",
              border: "1px solid #334155",
              padding: "16px 16px 12px",
              boxShadow: "0 20px 44px rgba(15,23,42,0.5)",
            }}
          >
            <button
              onClick={closeGallery}
              style={{
                position: "absolute",
                top: 8,
                right: 8,
                border: "none",
                background: "rgba(15,23,42,0.7)",
                color: "#fff",
                width: 32,
                height: 32,
                borderRadius: "50%",
                fontSize: 18,
                cursor: "pointer",
              }}
              aria-label="Close gallery"
            >
              ×
            </button>
            {galleryImages.length > 1 && (
              <>
                <button
                  onClick={showPrevGalleryImage}
                  style={{
                    position: "absolute",
                    left: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                    border: "none",
                    background: "rgba(15,23,42,0.7)",
                    color: "#fff",
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    fontSize: 20,
                    cursor: "pointer",
                  }}
                  aria-label="Previous image"
                >
                  ‹
                </button>
                <button
                  onClick={showNextGalleryImage}
                  style={{
                    position: "absolute",
                    right: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                    border: "none",
                    background: "rgba(15,23,42,0.7)",
                    color: "#fff",
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    fontSize: 20,
                    cursor: "pointer",
                  }}
                  aria-label="Next image"
                >
                  ›
                </button>
              </>
            )}
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 360 }}>
              <img
                src={galleryImages[galleryIndex]}
                alt={`Gallery ${galleryIndex + 1}`}
                style={{
                  maxWidth: "100%",
                  maxHeight: "72vh",
                  objectFit: "contain",
                  borderRadius: 10,
                  border: "1px solid #334155",
                  background: "#020617",
                }}
              />
            </div>
            {galleryImages.length > 1 && (
              <div
                style={{
                  marginTop: 10,
                  display: "flex",
                  gap: 8,
                  overflowX: "auto",
                  paddingBottom: 2,
                }}
              >
                {galleryImages.map((url, index) => (
                  <button
                    key={`gallery-thumb-${url}-${index}`}
                    onClick={() => setGalleryIndex(index)}
                    style={{
                      border: index === galleryIndex ? "2px solid #60a5fa" : "1px solid #334155",
                      borderRadius: 8,
                      padding: 0,
                      width: 68,
                      height: 52,
                      overflow: "hidden",
                      background: "#0b1324",
                      cursor: "pointer",
                      flex: "0 0 auto",
                    }}
                  >
                    <img
                      src={url}
                      alt={`Thumbnail ${index + 1}`}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
